// Main application

import { useState, useEffect, useCallback, useRef } from 'react';
import { RotateCcw, Save, Circle, MousePointer, Link, Trash2, Target, CircleQuestionMark, FileText, Waypoints, House } from 'lucide-react';

// Import custom hooks
import { useCamera } from './hooks/useCamera';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useGraphState } from './hooks/useGraphState';
import { useLLMConnection } from './hooks/useLLMConnection';
import { useFeedback } from './hooks/useFeedback';
import { useSaveLoad } from './hooks/useSaveLoad';
import { useNodeManipulation } from './hooks/useNodeManipulation';
import { useNodeSelection } from './hooks/useNodeSelection';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import { useTheme } from './hooks/useTheme';

// Import components
import CenteredPrompt from './components/CenteredPrompt';
import GenerationStatusBar from './components/GenerationStatusBar';
import NodeComponent from './components/NodeComponent';
import NodeDetailsPanel from './components/NodeDetailsPanel';
import Minimap from './components/Minimap';
import FeedbackModal from './components/FeedbackModal';
import NewPromptBox from './components/NewPromptBox';
import SaveLoadModal from './components/SaveLoadModal';
import ModelSelector from './components/ModelSelector';
import { Z } from './constants/zLayers';
import InstallationGuide from './components/InstallationGuide';
import DeletionStoreModal from './components/DeletionStoreModal';
import ConnectionManager from './components/ConnectionManager';
import SetupWizard from './components/SetupWizard/SetupWizard';
import ModelDownloadConsent from './components/ModelDownloadConsent';
import Notice from './components/Notice';
import RepoLink from './components/RepoLink';
import ThemeToggle from './components/ThemeToggle';

// Import constants and utilities
import {
  colorSchemes,
  darkColorSchemes,
  EDGE_COLORS,
  RESPONSE_MODES,
  RESPONSE_MODE_LABELS,
  DEFAULT_RESPONSE_MODE,
  RESPONSE_MODE_STORAGE_KEY,
  preferredResponseModeFor
} from './constants/graphConstants';
import { loadSetupConfig } from './utils/setupWizardUtils';
import { focusOffsetForPanel } from './utils/panelLayout';
import { buildQuotePrompt } from './utils/threadUtils';

// Must stay in sync with the modes useNodeSelection cycles through.
const CONTEXT_MODE_LABELS = {
  auto: { title: 'Auto (relevant nodes selected for you)' },
  manual: { title: 'Manual (click nodes to select)' },
  branch: { title: 'Branch (click a subtree)' },
  batch: { title: 'Batch (click a generation)' }
};

const Graphible = () => {
  // Core state
  const [preferences] = useState({
    colorScheme: 'blue',
    layoutStyle: 'hierarchical',
    animationSpeed: 1.0,
    nodeSize: 'medium'
  });

  const [nodeDetails, setNodeDetails] = useState(null);
  const [showPromptCenter, setShowPromptCenter] = useState(true);
  const [initialPromptText, setInitialPromptText] = useState('');
  const [isTypingPrompt, setIsTypingPrompt] = useState(false);
  const [showInstallationGuide, setShowInstallationGuide] = useState(false);
  const [showDeletionStore, setShowDeletionStore] = useState(false);
  const [showConnectionManager, setShowConnectionManager] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [isFirstRun, setIsFirstRun] = useState(false);

  // Whether a reply is split into several nodes or kept whole in one.
  const [responseMode, setResponseMode] = useState(() => {
    try {
      const saved = localStorage.getItem(RESPONSE_MODE_STORAGE_KEY);
      return Object.values(RESPONSE_MODES).includes(saved) ? saved : DEFAULT_RESPONSE_MODE;
    } catch {
      return DEFAULT_RESPONSE_MODE;
    }
  });

  const toggleResponseMode = useCallback(() => {
    setResponseMode(prev => {
      const next = prev === RESPONSE_MODES.GRAPH ? RESPONSE_MODES.SINGLE : RESPONSE_MODES.GRAPH;
      try {
        localStorage.setItem(RESPONSE_MODE_STORAGE_KEY, next);
      } catch {
        // Storage can be unavailable in private windows; the mode still applies.
      }
      return next;
    });
  }, []);

  const [notice, setNotice] = useState(null);

  // Guards the one-shot startup connection attempt (see the effect below).
  const hasInitializedConnection = useRef(false);

  // Mirrors connectionError so the submit handler can read the reason set
  // during the await it just made, without waiting for a re-render.
  const connectionErrorRef = useRef(null);

  // Custom hooks
  const { isDark, toggleTheme } = useTheme();
  const { camera, setCameraImmediate, setCameraTarget } = useCamera();

  const {
    llmConnected,
    currentModel,
    testLLMConnection,
    generateWithLLM,
    handleModelChange,
    loadSavedConfig,
    hasTestedInitially,
    webllmLoadingProgress,
    webllmLoadState,
    consentRequest,
    resolveConsentRequest,
    connectionError,
  } = useLLMConnection();

  // Switching to a backend that cannot do graph mode moves the user to single
  // mode once, rather than letting them discover it through a degraded result.
  const steeredBackendRef = useRef(null);
  useEffect(() => {
    const preferred = preferredResponseModeFor(currentModel);
    if (!preferred || steeredBackendRef.current === currentModel?.type) return;

    steeredBackendRef.current = currentModel?.type;
    setResponseMode(preferred);
    try {
      localStorage.setItem(RESPONSE_MODE_STORAGE_KEY, preferred);
    } catch {
      // Storage may be unavailable; the mode still applies to this session.
    }
  }, [currentModel]);


  const {
    nodes,
    connections,
    generationStatus,
    streamingContent,
    currentNodeId,
    currentStreamingNodeId,
    nodeMap,
    setCurrentNodeId,
    addNode,
    resetGraph,
    generateWithLLM: generateGraphWithLLM,
    applyLayoutOptimization,
    cancelGeneration,
    setConnections,
    setNodes
  } = useGraphState(generateWithLLM, setNotice);

  // Node manipulation and selection hooks
  const {
    draggingNodeId,
    isResizingNodeId,
    startNodeDrag,
    startNodeResize,
    deleteNode,
    restoreNode,
    permanentlyDeleteNode,
    deletedNodes,
    addConnection,
    removeConnection
  } = useNodeManipulation(nodes, setNodes, connections, setConnections);

  const {
    selectedNodeIds,
    contextMode,
    toggleContextMode,
    setContextMode,
    handleNodeSelection,
    updateAutoContext,
    toggleNodeSelection,
    isNodeSelected,
    clearSelections,
    selectedCount
  } = useNodeSelection();

  const {
    feedbackHistory,
    showFeedbackModal,
    setShowFeedbackModal,
    submitFeedback,
    getQuickFeedbackOptions
  } = useFeedback(generateWithLLM);

  const {
    savedGraphs,
    showSaveLoad,
    setShowSaveLoad,
    saveCurrentGraph,
    deleteGraph,
    exportGraph,
    importGraph
  } = useSaveLoad(nodes, connections, currentNodeId, initialPromptText);

  useCanvasInteraction({
    camera,
    setCameraImmediate,
    enabled: !showPromptCenter,
    isManipulatingNode: draggingNodeId !== null || isResizingNodeId !== null,
  });

  useKeyboardNavigation({
    nodes,
    currentNodeId,
    setCurrentNodeId,
    setNodeDetails,
    setCameraTarget,
    camera,
    setCameraImmediate,
    showPromptCenter,
    generationStatus,
    isTypingPrompt,
    showFeedbackModal: showFeedbackModal !== null
  });

  useEffect(() => {
    const setupConfig = loadSetupConfig();
    if (!setupConfig.isComplete) {
      setIsFirstRun(true);
      setShowSetupWizard(true);
    }
  }, []);

  useEffect(() => {
    const initializeConnection = async () => {
      const setupConfig = loadSetupConfig();

      if (setupConfig.isComplete && setupConfig.config) {
        // Use saved setup configuration
        const savedConfig = setupConfig.config;
        console.log('Using saved setup config:', savedConfig);

        if (savedConfig.type === 'external' && savedConfig.provider === 'google' && !savedConfig.apiKey) {
          const savedApiKey = localStorage.getItem('graphible-google-api-key');
          if (savedApiKey) {
            const updatedConfig = { ...savedConfig, apiKey: savedApiKey };
            handleModelChange(updatedConfig);
            console.log('Updated config with saved API key:', updatedConfig);
          }
        } else {
          handleModelChange(savedConfig);
        }

        if (!hasTestedInitially) {
          await testLLMConnection(savedConfig);
        }
      } else if (!showSetupWizard) {
        // Fallback to legacy configuration loading
        const legacyConfig = loadSavedConfig();
        console.log('App initialization - loaded legacy config:', legacyConfig);

        if (legacyConfig.type === 'external' && legacyConfig.provider === 'google' && !legacyConfig.apiKey) {
          const savedApiKey = localStorage.getItem('graphible-google-api-key');
          if (savedApiKey) {
            const updatedConfig = { ...legacyConfig, apiKey: savedApiKey };
            handleModelChange(updatedConfig);
            console.log('Updated legacy config with saved API key:', updatedConfig);
          }
        }

        if (!hasTestedInitially) {
          await testLLMConnection(legacyConfig);
        }
      }
    };

    // Run once. loadSavedConfig/handleModelChange/testLLMConnection all change
    // identity as a result of what this effect does - testLLMConnection depends
    // on currentModel, which handleModelChange sets - so keeping them as
    // dependencies re-fires the effect forever.
    if (!isFirstRun && !hasInitializedConnection.current) {
      hasInitializedConnection.current = true;
      initializeConnection();
    }
  }, [loadSavedConfig, handleModelChange, testLLMConnection, hasTestedInitially, isFirstRun, showSetupWizard]);

  const handleSetupComplete = useCallback((config) => {
    console.log('Setup completed with config:', config);
    setIsFirstRun(false);

    if (config.type !== 'demo') {
      handleModelChange(config);
      // Finishing the wizard is an explicit choice, so this test may download.
      setTimeout(() => testLLMConnection(config, { interactive: true }), 500);
    }
  }, [handleModelChange, testLLMConnection]);

  const handleSetupClose = useCallback(() => {
    setShowSetupWizard(false);

    // If this was first run and they closed without completing,
    // show the regular centered prompt
    if (isFirstRun) {
      setIsFirstRun(false);
      // The regular prompt will show since no model is configured
    }
  }, [isFirstRun]);

  const handleLoadDemoGraph = useCallback((demoData) => {
    // Load the demo graph data
    resetGraph();
    demoData.nodes.forEach(node => addNode(node));
    setConnections(demoData.connections || []);
    setCurrentNodeId(demoData.currentNodeId);
    setInitialPromptText(demoData.name);
    setShowPromptCenter(false);
    setNodeDetails(null);
    clearSelections();


    setCameraImmediate(0, 0, 1.0);
  }, [resetGraph, addNode, setConnections, setCurrentNodeId, setInitialPromptText, setShowPromptCenter,
    setNodeDetails, clearSelections, setCameraImmediate]);

  const handleShowSetupWizard = useCallback(() => {
    setShowSetupWizard(true);
  }, []);

  // Back to the start screen. Deliberately non-destructive: the graph stays in
  // state, so this is reversible from the start screen's own "Back to graph".
  // Submitting a new prompt there already calls resetGraph, so keeping the
  // nodes here cannot leak them into the next graph.
  const handleReturnToStart = useCallback(() => {
    setShowPromptCenter(true);
    setNodeDetails(null);
    setModelMenuOpen(false);
  }, [setShowPromptCenter, setNodeDetails, setModelMenuOpen]);

  // Update auto context when current node changes
  useEffect(() => {
    if (nodes.length > 0 && currentNodeId !== null) {
      updateAutoContext(nodes, currentNodeId, connections);
    }
  }, [nodes, currentNodeId, connections, updateAutoContext]);

  // Use UI personality color scheme, fall back to preferences, then default
  // Nodes and edges are painted with inline colors, which Tailwind's dark:
  // variant cannot reach, so the theme picks their palette here.
  const schemeKey = preferences.colorScheme || 'default';
  const currentScheme = isDark ? darkColorSchemes[schemeKey] : colorSchemes[schemeKey];
  const edgeColors = isDark ? EDGE_COLORS.dark : EDGE_COLORS.light;

  useEffect(() => {
    connectionErrorRef.current = connectionError;
  }, [connectionError]);

  // Node focusing. The details panel follows the node as it streams, so this
  // runs on every chunk - but the camera only moves when the focused node
  // actually changes, otherwise the canvas jitters throughout a generation.
  const focusedNodeIdRef = useRef(null);
  useEffect(() => {
    const currentNode = nodeMap.get(currentNodeId);
    if (!currentNode || showPromptCenter) return;

    if (focusedNodeIdRef.current !== currentNodeId) {
      focusedNodeIdRef.current = currentNodeId;
      setNodeDetails(currentNode);
      // Shift left by half the panel so the node lands in the visible half.
      setCameraImmediate(
        -currentNode.worldX - focusOffsetForPanel(camera.zoom, true),
        -currentNode.worldY
      );
      return;
    }

    // Same node: keep an open panel in step with its content while it streams,
    // but never reopen one the user has closed. This effect re-runs whenever
    // the camera moves, so an unconditional set made the close button useless.
    setNodeDetails(prev => (prev && prev.id === currentNodeId ? currentNode : prev));
  }, [currentNodeId, showPromptCenter, nodeMap, setCameraImmediate, camera.zoom]);


  // Layout optimization that preserves selections
  const applyLayoutOptimizationWithSelection = useCallback(() => {
    applyLayoutOptimization();
    if (nodes.length > 1) {

      // Restore selections after layout optimization
      setTimeout(() => {
        selectedNodeIds.forEach(nodeId => {
          if (nodes.some(n => n.id === nodeId)) {
            toggleNodeSelection(nodeId);
          }
        });
      }, 100);
    }
  }, [nodes, selectedNodeIds, toggleNodeSelection, applyLayoutOptimization]);

  // Event handlers
  const handleNodeClick = useCallback((node, event) => {
    const modifierKey = event?.ctrlKey || event?.metaKey;

    // Handle context selection based on mode
    handleNodeSelection(node.id, nodes, connections);

    // Always update current node (except in manual mode with modifier key)
    if (!(contextMode === 'manual' && modifierKey)) {
      setCurrentNodeId(node.id);
      setNodeDetails(node);
      setCameraImmediate(
        -node.worldX - focusOffsetForPanel(camera.zoom, true),
        -node.worldY,
        camera.zoom
      );
    }
  }, [camera.zoom, handleNodeSelection, nodes, connections, contextMode, setCurrentNodeId, setNodeDetails, setCameraImmediate]);

  // Branching from a quoted passage: the follow-up hangs off the node the
  // passage came from, and carries the quote as its context.
  const [pendingQuote, setPendingQuote] = useState(null);

  const handleBranchFromQuote = useCallback((quote, sourceNode) => {
    setPendingQuote({ quote, sourceNode });
    setCurrentNodeId(sourceNode.id);
    setIsTypingPrompt(true);
  }, [setCurrentNodeId]);

  const handleFeedback = (nodeId, isPositive) => {
    setShowFeedbackModal({ nodeId, isPositive });
  };

  const resetCamera = () => {
    setCameraTarget(0, 0, 1.0);
    const rootNode = nodes.find(n => n.type === 'root');
    if (rootNode) {
      setCurrentNodeId(rootNode.id);
      setNodeDetails(rootNode);
    }
  };

  const loadGraph = (graphData) => {
    resetGraph();
    graphData.nodes.forEach(node => addNode(node));
    // useSaveLoad persists connections; without this they were dropped on load.
    setConnections(graphData.connections || []);
    setCurrentNodeId(graphData.currentNodeId);
    setInitialPromptText(graphData.name);
    setShowPromptCenter(false);
    setShowSaveLoad(false);
    setNodeDetails(null);
    clearSelections();


    setCameraImmediate(0, 0, 1.0);
  };

  const handleInitialPromptSubmit = async (prompt) => {
    if (!prompt.trim()) return;

    if (llmConnected !== 'connected') {
      // interactive: the user has just asked for a generation, so this is an
      // acceptable moment to ask about a download.
      const isConnected = await testLLMConnection(currentModel, { interactive: true });

      if (!isConnected) {
        // Declining the download is a decision, not a failure: say nothing and
        // leave the user where they were.
        if (/declined|consent/i.test(connectionErrorRef.current || '')) return;

        const modelType = currentModel.type === 'local' ? 'local model (Ollama)' :
          currentModel.type === 'webllm' ? 'browser model' : 'external API';

        // Report it and carry on: the request may still succeed, and blocking
        // the page on a native dialog to ask helped nobody.
        setNotice({
          title: `Could not reach the ${modelType}`,
          detail: connectionErrorRef.current || undefined,
          hint: 'Trying anyway. Pick a different model from the menu at the top left if this keeps failing.',
        });
      }
    }

    resetGraph();
    clearSelections();
    setShowPromptCenter(false);

    await generateGraphWithLLM(prompt, null, null, currentModel, null, responseMode);
  };

  const enhancedGenerateWithLLM = async (prompt, prevWorldX, prevWorldY) => {
    const finalPrompt = pendingQuote
      ? buildQuotePrompt(pendingQuote.quote, prompt, pendingQuote.sourceNode?.label)
      : prompt;
    const source = pendingQuote?.sourceNode?.id ?? currentNodeId;
    setPendingQuote(null);

    return generateGraphWithLLM(finalPrompt, prevWorldX, prevWorldY, currentModel, source, responseMode);
  };

  return (
    <div className="w-screen h-screen relative bg-gradient-to-br from-slate-50 to-slate-100 dark:from-neutral-950 dark:to-[#050505] font-inter">
      <GenerationStatusBar
        generationStatus={generationStatus}
        streamingContent={streamingContent}
        onCancel={cancelGeneration}
      />

      <CenteredPrompt
        showPromptCenter={showPromptCenter}
        setShowPromptCenter={setShowPromptCenter}
        hasGraph={nodes.length > 0}
        onReturnToGraph={() => setShowPromptCenter(false)}
        llmConnected={llmConnected}
        onSubmit={handleInitialPromptSubmit}
        onShowSaveLoad={() => setShowSaveLoad(true)}
        onShowInstallationGuide={() => setShowInstallationGuide(true)}
        onShowSetupWizard={handleShowSetupWizard}
        currentModel={currentModel}
        onModelChange={handleModelChange}
        onTestConnection={testLLMConnection}
        webllmLoadingProgress={webllmLoadingProgress}
        webllmLoadState={webllmLoadState}
        responseMode={responseMode}
        onToggleResponseMode={toggleResponseMode}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        />

      {!showPromptCenter && (
        <>
          {/* Header */}
          {/* The header is a stacking context, so a menu opened inside it can
              never rise above the details panel on its own. Raising the header
              itself is what lets the menu's controls stay clickable. */}
          <div
            className="absolute top-0 left-0 right-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-b border-slate-200/50 dark:border-neutral-800/50 p-4 shadow-sm"
            style={{ zIndex: modelMenuOpen ? Z.DROPDOWN : Z.HEADER }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleReturnToStart}
                    className="flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800 hover:text-slate-800 dark:hover:text-neutral-100 transition-all duration-200"
                    title="Back to the start screen (your graph is kept)"
                    aria-label="Back to the start screen"
                  >
                    <House size={16} />
                  </button>
                  <h1 className="text-xl font-light text-slate-800 dark:text-neutral-100 tracking-tight">graph.ible</h1>
                </div>

                <ModelSelector
                  currentModel={currentModel}
                  onModelChange={handleModelChange}
                  connectionStatus={llmConnected}
                  onTestConnection={testLLMConnection}
                  onOpenChange={setModelMenuOpen}
                  webllmLoadingProgress={webllmLoadingProgress}
                  webllmLoadState={webllmLoadState}
                />
              </div>
              <button
                onClick={() => setShowSetupWizard(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 shadow-sm"
                style={{ fontSize: '12px' }}
                title="Reconfigure AI model"
              >
                <CircleQuestionMark size={16} />
                Wizard
              </button>

              <div className="flex items-center gap-3">
                {/* Mode toggle buttons */}
                <div className="flex bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-1">
                  <button
                    onClick={() => {
                      setContextMode('auto');
                    }}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-all duration-200 ${contextMode === 'auto'
                      ? 'bg-slate-100 dark:bg-neutral-800 text-slate-800 dark:text-neutral-100'
                      : 'text-slate-600 dark:text-neutral-300 hover:text-slate-800 dark:hover:text-neutral-100'
                      }`}
                    title="Normal mode"
                  >
                    <MousePointer size={14} />
                    Normal
                  </button>
                  <button
                    onClick={toggleContextMode}
                    className="flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-all duration-200 bg-indigo-100 dark:bg-indigo-500/15 text-indigo-800 dark:text-indigo-200"
                    title={`Context: ${CONTEXT_MODE_LABELS[contextMode]?.title ?? contextMode}`}
                  >
                    {contextMode === 'auto' && <><Circle size={14} />Auto</>}
                    {contextMode === 'manual' && <><MousePointer size={14} />Manual</>}
                    {contextMode === 'branch' && <><Link size={14} />Branch</>}
                    {contextMode === 'batch' && <><Target size={14} />Batch</>}
                    {selectedCount > 0 && (
                      <span className="bg-indigo-600 text-white px-1.5 py-0.5 rounded-full text-xs">
                        {selectedCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Response mode: split the reply into nodes, or keep it whole */}
                <button
                  onClick={toggleResponseMode}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shadow-sm ${responseMode === RESPONSE_MODES.SINGLE
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-800 dark:text-emerald-200'
                    : 'bg-white dark:bg-neutral-900 border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-800'
                    }`}
                  style={{ fontSize: '12px' }}
                  title={`${RESPONSE_MODE_LABELS[responseMode].name}: ${RESPONSE_MODE_LABELS[responseMode].description}. Click to switch.`}
                >
                  {responseMode === RESPONSE_MODES.SINGLE
                    ? <FileText size={16} />
                    : <Waypoints size={16} />}
                  {RESPONSE_MODE_LABELS[responseMode].name}
                </button>

                {/* Action buttons */}
                <button
                  onClick={applyLayoutOptimizationWithSelection}
                  disabled={nodes.length < 2}
                  style={{ fontSize: '12px' }}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 disabled:opacity-50 shadow-sm"
                >
                  <Circle size={16} />
                  Optimize Layout
                </button>

                <button
                  onClick={() => setShowConnectionManager(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 shadow-sm"
                  style={{ fontSize: '12px' }}
                >
                  <Link size={16} />
                  Connections
                </button>

                <button
                  onClick={() => setShowDeletionStore(true)}
                  style={{ fontSize: '12px' }}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 shadow-sm"
                >
                  <Trash2 size={16} /> Deleted ({deletedNodes.size})
                </button>

                <button
                  onClick={resetCamera}
                  style={{ fontSize: '12px' }}
                  className="flex items-center gap-2 px-2 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 shadow-sm"
                >
                  <RotateCcw size={16} />
                  Reset View
                </button>

                <button
                  onClick={() => setShowSaveLoad(true)}
                  style={{ fontSize: '12px' }}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 text-slate-600 dark:text-neutral-300 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 hover:border-slate-300 dark:hover:border-neutral-700 transition-all duration-200 shadow-sm"
                >
                  <Save size={16} />
                  Save/Load
                </button>

                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              </div>
            </div>
          </div>

          {/* Main Content */}
          {/* data-graph-canvas scopes wheel zoom to this element; overlays
              outside it scroll instead. See useCanvasInteraction. */}
          <div className="pt-20 w-full h-full relative overflow-hidden" data-graph-canvas="">
            {/* Background container that handles camera transformation */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                transform: `translate(${window.innerWidth / 2 + camera.x * camera.zoom}px, ${window.innerHeight / 2 + camera.y * camera.zoom}px) scale(${camera.zoom})`,
                transformOrigin: '0 0'
              }}
            >
              {/* SVG for connections - positioned in world coordinates */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  overflow: 'visible',
                  // The SVG coordinate system now matches world coordinates
                  left: -window.innerWidth / 2 / camera.zoom,
                  top: -window.innerHeight / 2 / camera.zoom,
                  width: window.innerWidth / camera.zoom,
                  height: window.innerHeight / camera.zoom
                }}
                viewBox={`${-window.innerWidth / 2 / camera.zoom} ${-window.innerHeight / 2 / camera.zoom} ${window.innerWidth / camera.zoom} ${window.innerHeight / camera.zoom}`}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                    markerUnits="userSpaceOnUse"
                  >
                    <polygon
                      points="0 0, 10 3.5, 0 7"
                      fill={edgeColors.stroke}
                    />
                  </marker>
                </defs>

                {connections.map((conn, index) => {
                  // By id, not array position: ids stop matching indices
                  // after any deletion.
                  const fromNode = nodeMap.get(conn.from);
                  const toNode = nodeMap.get(conn.to);

                  if (!fromNode || !toNode) return null;

                  const fromX = fromNode.worldX;
                  const fromY = fromNode.worldY;
                  const toX = toNode.worldX;
                  const toY = toNode.worldY;

                  const dx = toX - fromX;
                  const dy = toY - fromY;
                  const distance = Math.sqrt(dx * dx + dy * dy);

                  if (distance === 0) return null;

                  // Create curved path
                  const midX = (fromX + toX) / 2;
                  const midY = (fromY + toY) / 2;
                  const controlOffset = Math.min(distance * 0.15, 60);
                  const unitX = dx / distance;
                  const unitY = dy / distance;
                  const controlX = midX + (-unitY * controlOffset);
                  const controlY = midY + (unitX * controlOffset);

                  const path = `M${fromX},${fromY} Q${controlX},${controlY} ${toX},${toY}`;

                  return (
                    <g key={index}>
                      <path
                        d={path}
                        stroke={edgeColors.stroke}
                        strokeWidth={2 / camera.zoom} // Scale stroke width with zoom
                        fill="none"
                        strokeOpacity={edgeColors.opacity}
                        markerEnd="url(#arrowhead)"
                        strokeLinecap="round"
                        style={{
                          filter: edgeColors.shadow,
                        }}
                      />

                      <circle
                        cx={fromX}
                        cy={fromY}
                        r={4 / camera.zoom}
                        fill={edgeColors.start}
                        stroke={edgeColors.ring}
                        strokeWidth={2 / camera.zoom}
                        style={{
                          filter: edgeColors.shadow,
                        }}
                      />
                      <circle
                        cx={toX}
                        cy={toY}
                        r={4 / camera.zoom}
                        fill={edgeColors.end}
                        stroke={edgeColors.ring}
                        strokeWidth={2 / camera.zoom}
                        style={{
                          filter: edgeColors.shadow,
                        }}
                      />
                    </g>
                  );
                })}
              </svg>

              {/* Nodes positioned in world coordinates */}
              {nodes.map(node => (
                <div
                  key={node.id}
                  style={{
                    position: 'absolute',
                    left: node.worldX,
                    top: node.worldY,
                    transform: 'translate(-50%, -50%)' // Center the node on its world position
                  }}
                >
                  <NodeComponent
                    node={node}
                    isCurrent={node.id === currentNodeId}
                    isStreaming={currentStreamingNodeId === node.id}
                    isSelected={isNodeSelected(node.id)}
                    onClick={handleNodeClick}
                    onFeedback={handleFeedback}
                    colorScheme={currentScheme}
                    showPromptCenter={showPromptCenter}
                    generationStatus={generationStatus}
                    // Manipulation handlers
                    onStartDrag={startNodeDrag}
                    onStartResize={startNodeResize}
                    onDelete={deleteNode}
                    // Selection handlers
                    onToggleSelection={toggleNodeSelection}
                    camera={camera}
                  />
                </div>
              ))}

            </div>
          </div>

          {/* UI panels that stay in screen coordinates */}
          <NodeDetailsPanel
            nodeDetails={nodeDetails}
            onClose={() => setNodeDetails(null)}
            feedbackHistory={feedbackHistory}
            nodes={nodes}
            connections={connections}
            onBranchFromQuote={handleBranchFromQuote}
            onNavigateToNode={(id) => setCurrentNodeId(id)}
          />

          <Minimap
            nodes={nodes}
            connections={connections}
            currentNodeId={currentNodeId}
            camera={camera}
            isDark={isDark}
            onNavigateToNode={(nodeId) => {
              const node = nodes.find(n => n.id === nodeId);
              if (node) {
                setCurrentNodeId(nodeId);
                setNodeDetails(node);
                setCameraTarget(-node.worldX, -node.worldY);
              }
            }}
            onCameraMove={setCameraTarget}
          />
        </>
      )}

      <FeedbackModal
        showFeedbackModal={showFeedbackModal}
        onClose={() => setShowFeedbackModal(null)}
        onSubmit={submitFeedback}
        getQuickFeedbackOptions={getQuickFeedbackOptions}
      />

      <NewPromptBox
        currentNodeId={currentNodeId}
        nodeDetails={nodeDetails}
        generationStatus={generationStatus}
        onGenerate={enhancedGenerateWithLLM}
        isTypingPrompt={isTypingPrompt}
        setIsTypingPrompt={setIsTypingPrompt}
        quotedPassage={pendingQuote?.quote}
        nodes={nodes}
        connections={connections}
        selectedNodeIds={selectedNodeIds}
      />

      <SaveLoadModal
        showSaveLoad={showSaveLoad}
        savedGraphs={savedGraphs}
        hasNodes={nodes.length > 0}
        onClose={() => setShowSaveLoad(false)}
        onSave={saveCurrentGraph}
        onLoad={loadGraph}
        onDelete={deleteGraph}
        onExport={exportGraph}
        onImport={importGraph}
      />

      <InstallationGuide
        showGuide={showInstallationGuide}
        onClose={() => setShowInstallationGuide(false)}
      />

      {/* New Modals - will need to create these components */}
      {showDeletionStore && (
        <DeletionStoreModal
          isOpen={showDeletionStore}
          onClose={() => setShowDeletionStore(false)}
          deletedNodes={deletedNodes}
          onRestoreNode={restoreNode}
          onPermanentlyDeleteNode={permanentlyDeleteNode}
        />
      )}

      {showConnectionManager && (
        <ConnectionManager
          isOpen={showConnectionManager}
          onClose={() => setShowConnectionManager(false)}
          nodes={nodes}
          connections={connections}
          onAddConnection={addConnection}
          onRemoveConnection={removeConnection}
        />
      )}

      <RepoLink />

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <ModelDownloadConsent
        request={consentRequest}
        onDecide={resolveConsentRequest}
      />

      <SetupWizard
        isOpen={showSetupWizard}
        onClose={handleSetupClose}
        onComplete={handleSetupComplete}
        onModelChange={handleModelChange}
        onLoadDemoGraph={handleLoadDemoGraph}
      />

    </div>
  );
};

Graphible.displayName = 'Graphible';

export default Graphible;